<template>
  <div class="project-detail">
    <header>
      <div class="left page-title">
        <h2>{{ project.name }}</h2>
        <p class="d-flex align-items-center" v-tooltip.bottom-end="{ content: project.description, delay }">
          <span class="id"
            >ID:
            <span>{{ project.shortId }}</span>
          </span>
          <img v-if="project.private" class="mr-2" height="22px" width="22px" src="~@/assets/icons/lock.svg" />
          {{ project.description }}
        </p>
      </div>

      <div class="right">
        <a class="main-action" :style="loading_detail ? 'cursor: progress' : null" @click="loading_detail || edit_project('details')">Edit project</a>
      </div>
    </header>

    <div class="main">
      <div class="left">
        <div class="panel overview">
          <div class="flex-between">
            <div class="left">
              <h4 class="title">Project details</h4>

              <div class="overdue header" v-if="overdue && overdue_by">
                <img src="~@/assets/icons/alert.svg" />
                <span
                  >Overdue by {{ overdue_by }} day{{
                    overdue_by === 1 ? '' : 's'
                  }}</span
                >
              </div>

              <div class="feature">
                <donut
                  :percent="completion"
                  backgroundColor="#E4E7ED"
                  :foregroundColor="getColor(project.status)"
                  classValue="donut amount"
                  :strokeWidth="14"
                  :radius="90"
                  :width="84"
                  :height="84"
                  :visibleValue="true"
                  emptyText="0%"
                >
                  <span>{{ completion }}%</span>
                </donut>
                <span class="status" :class="project.status.toLowerCase()">
                  {{ project.status }}
                </span>
              </div>

              <span class="border"></span>

              <div class="feature">
                <div
                  class="date"
                  :class="{ unset: !project.startDate }"
                  @click="setDate('startDate')"
                >
                  <span>{{ formatDate(project.startDate, 'DD', 'DD') }}</span
                  ><span class="small">{{
                    formatDate(project.startDate, 'MMM YY', 'MMM YY')
                  }}</span>
                </div>
                <label>Start</label>
              </div>

              <i class="material-icons">arrow_forward</i>

              <div class="feature" :class="{ overdue }">
                <div
                  class="date"
                  :class="{ unset: !project.dueDate }"
                  @click="setDate('dueDate')"
                >
                  <span>{{ formatDate(project.dueDate, 'DD', 'DD') }}</span
                  ><span class="small">{{
                    formatDate(project.dueDate, 'MMM YY', 'MMM YY')
                  }}</span>
                </div>
                <label>Due</label>
              </div>
            </div>

            <project-members :project="project" class="project-members" />

            <div
              class="right empty_state"
              v-if="!project.projectMembers || !project.projectMembers.length"
              @click="edit_project('team')"
            >
              <img src="~@/assets/icons/add_information.svg" />
              <span>Add information</span>
            </div>
          </div>
        </div>

        <div class="panel stats">
          <div class="stat">
            <span>Estimated spend</span>
            <h3>{{ formatCurrency(project.estAnnualSpend) }}</h3>
          </div>

          <div class="stat">
<!--            TODO: Better UI Layout required as well as better check for benefit statuses-->
            <template v-if="benefitData && benefitData.total_benefits > 0">
              <span>
                Realised benefits
                <span
                  class="info-icon"
                  v-tooltip="{
                    content: realisedBenefitsPopoverContent,
                    delay,
                    classes: 'large',
                  }"
                  v-if="realisedBenefitsPopoverContent"
                ></span>
              </span>
              <h3>{{ formatCurrency(benefitData.total_benefits) }}</h3>
            </template>
            <template v-else>
              <span>
                Forecasted benefits
                <span
                  class="info-icon"
                  v-tooltip="{
                    content: forecastedBenefitsPopoverContent,
                    delay,
                    classes: 'large',
                  }"
                  v-if="forecastedBenefitsPopoverContent"
                ></span>
              </span>
              <h3>{{ formatCurrency(project.forecasted_benefits) }}</h3>
            </template>
          </div>

          <div class="stat">
            <template v-if="benefitData && benefitData.total_benefits > 0">
              <span>
                Current FY Realised Benefits
                <span
                  class="info-icon"
                  v-tooltip="{
                    content: realisedFyBenefitsPopoverContent,
                    delay,
                    classes: 'large',
                  }"
                  v-if="realisedFyBenefitsPopoverContent"
                ></span>
              </span>
              <h3>{{ formatCurrency(benefitData.fy_benefits) }}</h3>
            </template>
            <template v-else>
              <span>
                Current FY Forecasted Benefits
                <span
                  class="info-icon"
                  v-tooltip="{
                    content: forecastedFyBenefitsPopoverContent,
                    delay,
                    classes: 'large',
                  }"
                  v-if="forecastedFyBenefitsPopoverContent"
                ></span>
              </span>
              <h3>{{ formatCurrency(project.forecasted_fy_benefits) }}</h3>
            </template>
          </div>

          <project-members :project="project" class="project-members" />

          <div
            class="right empty_state"
            v-if="!project.projectMembers || !project.projectMembers.length"
            @click="edit_project('team')"
          >
            <img src="~@/assets/icons/add_information.svg" />
            <span>Add information</span>
          </div>
        </div>

        <div class="panel actions">
          <h4 class="title">Project stages</h4>

          <a
            class="action generate"
            @click="askIfShouldGenerateActions()"
            v-if="project.type !== 'Other' && !(loading_actions || action_groups.length)"
            ><i class="material-icons">flash_on</i></a
          >
          <a class="action" v-show="!loading_actions"
            ><img src="~@/assets/icons/plus-grey.svg" @click="newGroup()"
          /></a>

          <div v-show="loading_actions" class="saving">
            <img src="~@/assets/icons/spinner.svg" />
            <p>Loading</p>
          </div>

          <accordion
            v-show="!loading_actions"
            :groups="action_groups"
            :project_id="project.id"
          />
        </div>
      </div>

      <div class="right">
        <div class="panel">
          <h4 class="title">Project type: {{ project.type }}</h4>

          <div
            class="empty_state"
            v-if="projectTypeEmpty()"
            @click="edit_project('type')"
          >
            <img src="~@/assets/icons/type_empty.svg" />
            <span>Add details</span>
          </div>

          <div class="list type-list">
            <div
              class="item"
              v-if="project.rfpReleaseDate && project.type === 'Tender'"
            >
              <label>RFP release date:</label>
              <p>{{ formatDate(project.rfpReleaseDate, '—') }}</p>
            </div>

            <div
              class="item"
              v-if="project.rfpResponsesDueDate && project.type === 'Tender'"
            >
              <label>Responses due:</label>
              <p>{{ formatDate(project.rfpResponsesDueDate, '—') }}</p>
            </div>

            <div
              class="item"
              v-if="
                project.negPlanCompleted &&
                  (project.type === 'Tender' || project.type === 'Negotiation')
              "
            >
              <label>Negotiation plan completed:</label>
              <p>{{ formatDate(project.negPlanCompleted, '—') }}</p>
            </div>

            <div
              class="item"
              v-if="
                project.anticipatedAwardDate &&
                  (project.type === 'Tender' || project.type === 'Negotiation')
              "
            >
              <label>Anticipated award date:</label>
              <p>{{ formatDate(project.anticipatedAwardDate, '—') }}</p>
            </div>

            <div
              class="item"
              v-if="project.projectSuppliers && project.projectSuppliers.length"
            >
              <label>Suppliers:</label>
              <p>{{ project.projectSuppliers.length }}</p>
            </div>

            <user
              v-for="supplier of project.projectSuppliers"
              :key="'supplier-' + supplier.id"
              :users="[supplier]"
              :text="getSupplierLabel(supplier.type)"
              :className="
                `${supplier.type === 'Selected' ? 'selected' : ''} indented`
              "
            />
          </div>

          <div
            class="list type-list"
            v-if="project.type === 'Contract Renewal'"
          >
            <div class="item" v-if="project.anticipatedRenewalDate">
              <label>Anticipated renewal date:</label>
              <p>{{ formatDate(project.anticipatedRenewalDate, '—') }}</p>
            </div>
          </div>

          <div class="list type-list" v-if="project.type === 'Exit'">
            <div class="item" v-if="project.anticipatedNoticeDate">
              <label>Anticipated notice date:</label>
              <p>{{ formatDate(project.anticipatedNoticeDate, '—') }}</p>
            </div>

            <div class="item" v-if="project.anticipatedExitDate">
              <label>Anticipated exit date:</label>
              <p>{{ formatDate(project.anticipatedExitDate, '—') }}</p>
            </div>
          </div>
        </div>

        <div
          class="panel"
          :class="{ 'has-footer': documents.length > documents_short.length }"
        >
          <h4 class="title">
            Documents
            <span v-if="Object.keys(documents).length || (project.projectFiles && project.projectFiles.length)"
              >({{ Object.keys(documents).length + (project.projectFiles ? project.projectFiles.length : 0) }})</span
            >
          </h4>
          <a class="action" @click="edit_project('documents')"
            ><img src="~@/assets/icons/edit.svg"
          /></a>

          <div v-show="loading_detail" class="saving">
            <img src="~@/assets/icons/spinner.svg" />
            <p>Loading</p>
          </div>

          <div class="list" v-show="!loading_detail">
            <div
              class="item link"
              v-for="doc of documents_short"
              :key="doc.id"
              @click="openLink(doc.url)"
            >
              <a class="icon" :class="getDocType(doc)"></a>
              <img class="go" src="~@/assets/icons/go.svg" />
              <div class="stack">
                <h6>{{ doc.title }}</h6>
                <span class="desc">{{ doc.url }}</span>
                <a class="click">Click to open</a>
              </div>
            </div>

            <div
              class="item link"
              v-for="file of project.projectFiles"
              :key="file.id"
              @click="downloadFile(file)"
            >
              <a class="icon"></a>
              <div class="stack">
                <h6>{{ file.filename }}</h6>
                <span v-if="file.createdById && users[file.createdById]" class="desc">
                  {{ `Uploaded by ${users[file.createdById].name}`}}
                </span>
                <a class="click">Click to download</a>
              </div>
            </div>
          </div>
          <div class="view-more" @click="edit_project('documents')">
            View more
          </div>

          <div
            class="empty_state"
            v-if="
              !Object.keys(documents) ||
                (!Object.keys(documents).length && !loading_detail)
            "
            @click="edit_project('documents')"
          >
            <img src="~@/assets/icons/documents_empty.svg" />
            <span>Add documents</span>
          </div>
        </div>

        <div class="panel">
          <h4 class="title">
            Related projects
            <span
              v-if="project.relatedProjects && project.relatedProjects.length"
              >({{ project.relatedProjects.length }})</span
            >
          </h4>

          <add-multi-dropdown
            class="action"
            :options="project_options"
            :values="project.relatedProjectsIds"
            :onChange="editRelatedProjects"
            icon="edit"
            showSearch
          />

          <div class="list" v-if="project.relatedProjects">
            <div
              class="item link"
              v-for="related of project.relatedProjects"
              :key="related.id"
              @click="gotoProject(related.shortId)"
            >
              <div class="icon project"></div>
              <img class="go" src="~@/assets/icons/go.svg" />
              <div class="stack">
                <h6>{{ related.name }}</h6>
                <span class="desc">Click to open</span>
                <span class="click">Click to open</span>
              </div>
            </div>
          </div>
        </div>
        <div class="panel">
          <h4 class="title">
            Project updates
          </h4>
          <div v-if="loading_detail" class="saving">
            <img src="~@/assets/icons/spinner.svg" />
            <p>Loading</p>
          </div>
          <project-updates
            v-else
            v-model="project.projectUpdates"
            :projectId="project.id"
            :userIsMember="userIsMember"
          />
        </div>
      </div>
    </div>
    <overflow-drop />
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import currency from '../utils/currency';
import User from '../components/User.vue';
import Accordion from '../components/Accordion.vue';
import ProjectMembers from '../components/ProjectMembers.vue';
import OverflowDrop from '../components/OverflowDrop';
import AddMultiDropdown from '../components/AddMultiDropdown';
import benefitsPopover from '../utils/benefitsPopover';
import DoughnutChart from 'vue-doughnut-chart';
import ProjectUpdates from '../components/ProjectUpdates.vue';
import { mapGetters } from 'vuex';
import API from "@/api";

export default {
  name: 'ProjectDetail',
  data() {
    return {
      delay: { show: 700, hide: 0 },
      loading_detail: false,
      loading_actions: false,
      showingPrompt: false,
      havePromptedMarkActive: false,
    };
  },
  computed: {
    ...mapGetters(['currencyCodeById', 'users']),
    app_user() {
      return this.$store.getters.app_user || {};
    },
    userIsMember() {
      return (
        this.project &&
        this.project.projectMembers &&
        !!this.project.projectMembers.find(
          member => member.id === this.app_user.id
        )
      );
    },
    page() {
      return this.$store.getters.page;
    },
    today() {
      return moment().startOf('d');
    },
    project_id() {
      return this.$route.params.id;
    },
    projects() {
      return this.$store.getters.projects;
    },
    project() {
      if (!this.projects || !this.project_id) {
        return null;
      }
      return this.projects[this.project_id];
    },
    year() {
      return moment().format('YYYY');
    },
    action_groups() {
      // TODO - change the default param below
      let groups = this.$store.getters.actions || [];
      groups.forEach(group => {
        group.actions = group.actions.sort(this.sortByDate);
      });
      return groups;
    },
    all_actions() {
      let all = [];
      this.action_groups.forEach(group => {
        all = [...all, ...group.actions];
      });
      return all;
    },
    completed() {
      if (!this.all_actions || !this.all_actions.length) {
        return 0;
      }

      let count = 0;

      this.all_actions.forEach(action => {
        if (action.completed) {
          count++;
        }
      });

      return count;
    },
    completion() {
      if (!this.all_actions || !this.all_actions.length) {
        // if the project is marked as completed, we can return 100%, else it's automatically 0
        return (this.project && this.project.status === 'Completed') ? 100 : 0;
      }
      return Math.round((100 * this.completed) / this.all_actions.length);
    },
    overdue() {
      if (!this.project || !this.project.dueDate) {
        return false;
      }
      const due_day = moment(this.project.dueDate).startOf('d');
      return due_day.isBefore(this.today) && this.project.status === 'Active';
    },
    overdue_by() {
      if (!this.overdue) {
        return '';
      }

      const due_day = moment(this.project.dueDate).startOf('d');
      return this.today.diff(due_day, 'd');
    },
    forecastedBenefitsPopoverContent() {
      return benefitsPopover(this.project, true);
    },
    forecastedFyBenefitsPopoverContent() {
      return benefitsPopover(this.project, true, true);
    },
    realisedBenefitsPopoverContent() {
      return benefitsPopover(this.project, false);
    },
    realisedFyBenefitsPopoverContent() {
      return benefitsPopover(this.project, false, true);
    },
    benefitData() {
      let total_benefits = 0;
      let fy_benefits = 0;
      let forecasted_benefits = 0;
      let forecasted_fy_benefits = 0;

      const thisYear = new Date().getFullYear();
      const fy_month = this.app_user.organization.fy_month;

      let current_fy_end = moment(fy_month + ' ' + thisYear, 'MMMM YYYY').subtract(
        1,
        'd'
      );
      if (moment().isAfter(current_fy_end)) {
        current_fy_end = current_fy_end.add(1, 'y');
      }

      this.project.projectBenefits.forEach(benefit => {
        total_benefits += benefit.amount;
        forecasted_benefits += benefit.forecastedAmount;

        const benefit_year = moment(benefit.yearEnding, 'YYYY');
        const is_current_year = current_fy_end.isSame(benefit_year, 'y');

        if (is_current_year) {
          fy_benefits += benefit.amount;
          forecasted_fy_benefits += benefit.forecastedAmount;
        }
      });
      return {
        total_benefits,
        fy_benefits,
        forecasted_benefits,
        forecasted_fy_benefits
      }
    },
    documents() {
      if (!this.project || !this.project.projectDocuments) return [];
      return this.project.projectDocuments;
    },
    documents_short() {
      return this.documents.filter((doc, index) => {
        return index < 5;
      });
    },
    all_projects() {
      return this.$store.getters.all_projects;
    },
    project_options() {
      return Object.keys(this.all_projects)
        .filter(key => key !== this.project_id)
        .map(key => {
          return {
            text: this.all_projects[key].name,
            value: this.all_projects[key].id,
            shortId: this.all_projects[key].shortId,
          };
        });
    },
  },
  methods: {
    openLink(link) {
      window.open(link, '_blank');
    },
    downloadFile(file){
      const { key } = file
      if(!key) return;

      API.getS3GETURL(key)
        .then(({signedURL}) => {
          API.downloadFile(signedURL, file.filename);
        })
        .catch((e) => {
          console.error(e)
        })
    },
    edit_project(view) {
      this.$store.dispatch('setProjectModalID', this.project.shortId);

      if (view) {
        this.$store.dispatch('setProjectModalView', view);
      }
    },
    sortByDate(a, b) {
      const momentA = a.dueDate ? moment(a.dueDate) : moment('3000-01-01');
      const momentB = b.dueDate ? moment(b.dueDate) : moment('3000-01-01');

      if (momentA.isSame(momentB, 'd')) {
        return this.sortById(a, b);
      }
      if (momentA.isBefore(momentB)) {
        return -1;
      }
      return 1;
    },
    sortById(a, b) {
      if (a.id < b.id) {
        return -1;
      }
      return 1;
    },
    formatDate(date, fallback, format) {
      format = format || 'DD MMM YYYY';
      if (!date) {
        return fallback;
      }
      return moment(date).format(format);
    },
    formatCurrency(num) {
      if (!this.project) {
        return '0';
      }
      currency.locale(this.currencyCodeById(this.project.currencyId));
      return currency(num).format('$0a.[0]');
    },
    setDate(key) {
      if (!this.project[key]) {
        this.edit_project('details');
      }
    },
    openModal(view) {
      this.$store.dispatch('setProjectModalView', view);
      this.$store.dispatch('setProjectModalID', this.project.id);
    },
    getColor(status) {
      switch (status) {
        case 'Completed':
          return '#5BA0EB';
        case 'Cancelled':
          return '#E78180';
        case 'On hold':
          return '#FF8457';
        case 'Pipeline':
          return '#727780';
        case 'Active':
        default:
          return '#65CDB5';
      }
    },
    getDocType(doc) {
      return doc.type.toLowerCase() || '';
    },
    projectTypeEmpty() {
      let empty = false;

      if (
        !this.project.projectSuppliers.length &&
        !this.project.rfpReleaseDate &&
        !this.project.rfpResponsesDueDate &&
        !this.project.negPlanCompleted &&
        !this.project.anticipatedAwardDate &&
        !this.project.anticipatedRenewalDate &&
        !this.project.anticipatedNoticeDate &&
        !this.project.anticipatedExitDate
      ) {
        empty = true;
      }

      return empty;
    },
    async newGroup() {
      const newGroup = {
        title: 'Stage title',
        projectId: this.project.id,
        startDate: null,
        endDate: null,
        actions: [],
      };
      const saved_group = await this.$store.dispatch('createActionGroups', {
        actionGroups: [newGroup],
        project_id: this.project.id,
      });
      const id = saved_group.data.createdActionGroups[0].id;
      setTimeout(() => {
        document.querySelector(`#panel-title-${id}`).select();
        const main = document.querySelector(`#main-view`);
        main.scroll({ top: main.scrollHeight, left: 0, behavior: 'smooth' });
      }, 600);
    },
    async editRelatedProjects(val) {
      let related = this.project.relatedProjectsIds || [];
      if (related.includes(val)) {
        related = related.filter(id => id !== val);
      } else {
        related.push(val);
      }

      Vue.set(
        this.$store.getters.projects[this.project_id],
        'relatedProjectsIds',
        related
      );

      await this.$store.dispatch(
        'saveProject',
        this.$store.getters.projects[this.project_id]
      );
      await this.$store.dispatch('getProjectDetail', this.project.id);
    },
    async gotoProject(id) {
      if (!this.projects[id]) {
        const _id = this.project_options.find(d => d.shortId === id).value;
        await this.$store.dispatch('getProjectDetail', _id);
      }
      this.$router.push({ name: 'ProjectDetail', params: { id } });
      document.querySelector('#main-view').scrollTo(0, 0);
    },
    async refresh() {
      if (this.project.id) {
        try {
          this.loading_detail = true;
          await this.$store.dispatch('getProjectDetail', this.project.id);
          this.loading_detail = false;

          this.loading_actions = true;
          await this.$store.dispatch('getProjectActions', {
            id: this.project.id,
            request_time: moment(),
          });
          this.loading_actions = false;

          this.$ga.page('/projects/' + this.project.shortId);
        } catch (e) {
          console.error(e);
          this.loading_detail = false;
          this.loading_actions = false;
        }
      }
    },
    async askIfShouldGenerateActions() {
      try {
        const options = {
          html: false,
          okText: 'Yes',
          cancelText: 'No thanks',
          customClass: 'acada-confirm',
          animation: 'fade',
        };

        const message = {
          title: 'Generate tasks',
          body:
            'Would you like to generate a list of stages and tasks, based on your project type?',
        };

        await this.$dialog.confirm(message, options);
        this.generateActions();
      } catch (e) {
        // they clicked cancel, so don't close the modal
      }
    },
    async generateActions() {
      try {
        this.loading_actions = true;
        const id = this.project.id;
        await this.$store.dispatch('saveTemplatedActions', {
          type: this.project.type,
          project_id: id,
        });
        await this.$store.dispatch('getProjectActions', {
          id,
          request_time: moment(),
        });
        this.loading_actions = false;
      } catch (e) {
        console.error(e);
        this.loading_actions = false;
      }
    },
    async get_project_options() {
      if (!Object.keys(this.all_projects).length) {
        await this.$store.dispatch('getAllProjects');
      }
    },
    async promptMarkCompleted() {
      try {
        if (this.showingPrompt) return;
        const options = {
          html: false,
          okText: 'Yes',
          cancelText: 'No thanks',
          customClass: 'acada-confirm',
          animation: 'fade',
        };

        const message = {
          title: 'Mark project Completed',
          body: `All tasks for this project are complete. Would you like to mark the project as Completed?`,
        };

        this.showingPrompt = true;
        this.havePromptedMarkActive = false;
        await this.$dialog.confirm(message, options);
        this.showingPrompt = false;
        Vue.set(this.project, 'status', 'Completed');
        await this.$store.dispatch('saveProject', this.project);
      } catch (e) {
        console.log(e);
        this.showingPrompt = false;
        // they clicked cancel, so don't close the modal
      }
    },
    async promptMarkActive() {
      try {
        if (this.showingPrompt) return;
        const options = {
          html: false,
          okText: 'Yes',
          cancelText: 'No thanks',
          customClass: 'acada-confirm',
          animation: 'fade',
        };

        const message = {
          title: 'Mark project Active',
          body: `Your project is marked as Completed, but you still have tasks due. Would you like to mark this project as Active?`,
        };

        this.showingPrompt = true;
        this.havePromptedMarkActive = true;
        await this.$dialog.confirm(message, options);
        this.showingPrompt = false;
        Vue.set(this.project, 'status', 'Active');
        await this.$store.dispatch('saveProject', this.project);
      } catch (e) {
        console.log(e);
        this.showingPrompt = false;
        // they clicked cancel, so don't close the modal
      }
    },
    getSupplierLabel(type) {
      if (type === 'Selected') {
        switch (this.project.type) {
          case 'Contract Renewal':
            return 'Incumbent supplier';
          case 'Exit':
            return 'Exiting supplier';
          default:
            return 'Selected';
        }
      }
      return '';
    },
  },
  components: {
    user: User,
    accordion: Accordion,
    'project-members': ProjectMembers,
    donut: DoughnutChart,
    'overflow-drop': OverflowDrop,
    'add-multi-dropdown': AddMultiDropdown,
    ProjectUpdates,
  },
  async created() {
    this.refresh();
    this.get_project_options();
  },
  watch: {
    project_id() {
      this.refresh();
    },
    completion(newValue) {
      setTimeout(() => {
        if (this.completion === 100 && this.havePromptedMarkActive)
          this.havePromptedMarkActive = false;
        if (this.completion >= 100 && this.project.status !== 'Completed') {
          this.promptMarkCompleted();
        } else if (
          this.completion < 100 &&
          this.project.status === 'Completed' &&
          !this.havePromptedMarkActive
        ) {
          this.promptMarkActive();
        }
      }, 1500);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/_styles.scss';

.project-detail {
  position: relative;

  .main {
    display: flex;
    justify-content: space-between;
    align-items: stretch;

    .panel {
      margin-top: 30px;
      padding: 25px;
      font-size: 14px;
      position: relative;

      &.actions {
        padding-bottom: 0;
      }

      .title {
        color: #26292f;
        font-weight: 500;
        font-size: 16px;
        margin-bottom: 18px;

        span {
          color: #c6d0db;
        }
      }

      .action {
        position: absolute;
        top: 22px;
        right: 25px;
        cursor: pointer;
        min-width: auto;

        &.material-select {
          top: -6px;
          right: 15px;
        }

        &.generate {
          right: 50px;
          color: #979797;
          top: 24px;

          i {
            font-size: 20px;
          }
        }
      }

      .tag {
        background: $panel-border-color;
        color: $light-text-color;
        border-radius: 3px;
        height: 25px;
        padding: 2px 14px 5px;
        display: inline-block;
        margin: 5px 0;
        width: auto;

        &:last-child {
          margin-bottom: 0;
        }
      }

      &.overview {
        padding: 0;
      }

      &.overview .flex-between {
        align-items: stretch;
        flex-direction: row;

        .left {
          flex: 1;
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          position: relative;
          min-height: 300px;

          .border {
            border: none !important;
            border-right: 1px solid #e2e5ec !important;
            height: 48px;
            margin: 0 15px;
          }

          .title {
            position: absolute;
            top: 30px;
            left: 25px;
          }

          i {
            font-size: 18px;
          }

          .feature {
            margin: 0 10px;
            position: relative;

            .date {
              border-radius: 50%;
              width: 84px;
              height: 84px;
              background: #f7f7f7;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-wrap: wrap;

              &.unset {
                span {
                  opacity: 0.7;
                }

                cursor: pointer;
              }

              span {
                margin: 0;
                color: #626d79;
                font-weight: 500;
                font-size: 24px;
                width: 100%;
                text-align: center;
                margin-top: 15px;
              }

              .small {
                font-size: 11px;
                font-weight: 400;
                text-transform: uppercase;
                margin-top: -5px;
                margin-bottom: 20px;
              }
            }

            &.overdue {
              .date {
                background: $red-light;
                span {
                  color: $red;
                }
              }

              label {
                color: $red;
              }
            }

            label {
              text-align: center;
              text-transform: uppercase;
              width: 100%;
              margin-top: 20px;
              margin-bottom: 0;
              font-weight: 500;
              color: #c2c7d4;
              font-size: 13px;
              position: absolute;
            }

            .status {
              position: absolute;
              margin-top: 20px;
              margin-bottom: 0;
              left: 50%;
              transform: translateX(-50%);
              white-space: nowrap;
            }
          }
        }

        .empty_state {
          background: #fbfbfc;
          min-width: 262px;
          max-width: 34%;
          margin-left: 0;
        }

        @media screen and (max-width: 1120px) {
          .project-members,
          .empty_state {
            display: none;
          }
        }
      }
    }

    .left {
      width: calc(100% - 364px);
    }

    .right {
      width: 334px;
    }
  }
}

.flex-title {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-direction: row;
  flex-wrap: nowrap;
  position: relative;

  .status {
    margin: 0 8px;
  }
}

.flex-grid {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: nowrap;

  .column {
    flex: 1;
  }

  .label {
    display: flex;

    &:first-child {
      margin-top: 15px;
    }

    label {
      color: #acb6c1;
      margin-right: 3px;
      font-weight: 400;
      font-size: 14px;
      width: 75px;
    }
  }
}

.stats {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 0 !important;

  .project-members,
  .empty_state {
    display: none;
    align-self: flex-end;
    height: 355px;
    flex: 1;
    margin-left: 0 !important;
    justify-content: center;
  }

  .empty_state {
    background: #fbfbfc;
    min-width: 262px;
    max-width: 34%;
  }

  @media screen and (max-width: 1120px) {
    flex-direction: column;
    max-height: 500px;

    .project-members {
      display: flex;
    }

    .empty_state {
      display: flex;
    }
  }

  .stat {
    flex: 1;
    border-right: 1px solid $panel-border-color;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin: 30px 10px;

    @media screen and (max-width: 1120px) {
      border: none;
      width: calc(100% - 282px);
      margin: 20px 10px;

      &:nth-child(1) {
        margin-top: 40px;
      }

      &:nth-child(3) {
        margin-bottom: 40px;
      }
    }

    &:nth-child(3) {
      border-right: none;
    }

    span {
      color: #c2c7d4;
      text-transform: uppercase;
      font-size: 13px;
      font-weight: 500;
    }

    h3 {
      font-size: 38px;
      color: #26292f;
      font-weight: 500;
      margin: 0;
      text-align: center;
    }
  }
}

.page-title {
  .id {
    font-size: 13px;
    color: #676a6f;
    border-radius: 4px;
    background: rgba(172, 182, 193, 0.33);
    margin-right: 10px;
    padding: 3px 8px 4px;
    span {
      color: #26292f;
    }
  }
}

.type-list {
  align-items: stretch;
}

.overdue.header {
  position: absolute;
  top: 28px;
  left: 145px;
  background: $red;
  color: white;
  font-weight: 500;
  font-size: 13px;
  padding: 2px 8px 3px;
  border-radius: 4px;
  user-select: none;

  img {
    margin-right: 3px;
    position: relative;
    top: -2px;
  }

  span {
    color: white;
  }
}

.empty_state {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  flex-wrap: nowrap;
  cursor: pointer;

  span {
    margin: 15px auto 0;
    color: #c3c8d5;
    font-size: 16px;
    font-weight: 500;
    text-align: center;
    user-select: none;
  }

  img {
    margin: 0 auto;
    height: 74px;
    user-select: none;
  }
}

.info-icon {
  background-repeat: no-repeat;
  background-position: top center;
  background-size: 17px 34px;
  background-image: url(~@/assets/icons/info_ss.svg);
  width: 17px;
  height: 17px;
  cursor: pointer;
  display: inline-block;
  position: relative;
  top: 3px;
  left: 1px;

  &:hover {
    background-position: top -17px center;
  }
}

.saving {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  flex-wrap: nowrap;

  img {
    width: 40px;
    height: 40px;
    animation: spin 1.5s linear infinite;
  }

  p {
    font-weight: 400;
    font-size: 14px;
    color: #565b64;
    margin: 5px 0 30px;
  }
}

@keyframes spin {
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
</style>
